import React, { useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import './styles.css'
import App from './App'
import AppAndroid from './AppAndroid'
import { Stats } from '@react-three/drei'

const isIphone = typeof navigator !== 'undefined' && /iPhone/.test(navigator.userAgent);
const isAndroid = typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent);
// Détection Android avec résolution faible (≤ 1080x2412)
const screenWidth = window.screen.width;
const screenHeight = window.screen.height;
const maxResolution = Math.max(screenWidth, screenHeight);
const isLowEndAndroid = isAndroid && maxResolution <= 2412;

// Composant pour l'animation de texte type terminal
function TypingText({ text, delay = 0, speed = 80, style, keepCursor = false }) {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // Délai avant de commencer l'animation
    const startTimeout = setTimeout(() => {
      let index = 0;
      const typingInterval = setInterval(() => {
        if (index <= text.length) {
          setDisplayedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typingInterval);
          setIsComplete(true);
        }
      }, speed);

      return () => clearInterval(typingInterval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, delay, speed]);

  // Clignotement du curseur
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <span style={style}>
      {displayedText}
      {(!isComplete || keepCursor) && (
        <span style={{
          opacity: showCursor ? 1 : 0,
          transition: 'opacity 0.1s',
          color: '#35c19f'
        }}>
          _
        </span>
      )}
    </span>
  );
}

// Empêcher le comportement de scroll élastique sur iOS
if (isIphone) {
  // Empêcher le pull-to-refresh et le scroll bounce
  document.addEventListener('touchmove', (e) => {
    if (e.touches.length > 1) {
      // Permettre le pinch-to-zoom
      return;
    }
    // Empêcher le scroll par défaut
    e.preventDefault();
  }, { passive: false });

  // Empêcher le double-tap pour zoomer
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      e.preventDefault();
    }
    lastTouchEnd = now;
  }, false);
}

function Overlay({ loadingComplete }) {
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth)
  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)")
    const handler = e => setIsPortrait(e.matches)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [])

  // Détection des petites résolutions (Android entrée de gamme)
  const screenHeight = window.innerHeight;
  const isSmallScreen = screenHeight < 2000; // Pour 720x1600, 1080x2412, etc.

  const fontSize = isIphone ? '11px' : '13px';
  const offset = isIphone ? 20 : 40;
  // Augmenter l'espacement vertical sur petits écrans pour éviter le chevauchement
  const line1Offset = isIphone && isPortrait
    ? offset + 20
    : isSmallScreen && isPortrait
      ? offset + 50
      : offset;

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      {/* === HAUT - Tout s'anime en même temps === */}

      {/* Haut gauche - LUMEN OBSCURA avec curseur permanent */}
      {loadingComplete && (
        <TypingText
          text="LUMEN OBSCURA"
          delay={300}
          speed={80}
          keepCursor={true}
          style={{
            position: 'absolute',
            top: offset + 15,
            left: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}

      {/* Haut gauche - PARIS BASED CREATIVE STUDIO */}
      {loadingComplete && (
        <TypingText
          text="PARIS BASED CREATIVE STUDIO"
          delay={300}
          speed={50}
          style={{
            position: 'absolute',
            top: offset + 30,
            left: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}

      {/* Haut droite - LOUSSAIEF TIJANI */}
      {loadingComplete && (
        <TypingText
          text="LOUSSAIEF TIJANI  "
          delay={300}
          speed={70}
          style={{
            position: 'absolute',
            top: offset,
            right: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}

      {/* Haut droite - EMAIL (cliquable) */}
      {loadingComplete && (
        <a
          href="mailto:tijani@lumenobscura.com"
          style={{
            textDecoration: 'none',
            color: 'inherit',
            position: 'absolute',
            top: offset + 15,
            right: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        >
          <TypingText
            text="TIJANI@LUMENOBSCURA.COM"
            delay={300}
            speed={45}
            style={{
              fontFamily: "'Codystar', cursive"
            }}
          />
        </a>
      )}

      {/* Haut droite - TELEPHONE */}
      {loadingComplete && (
        <TypingText
          text="+33 6 21 86 43 34  "
          delay={300}
          speed={60}
          style={{
            position: 'absolute',
            top: offset + 30,
            right: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}

      {/* === BAS - Apparaissent après le haut === */}

      {/* Bas gauche - SPACE SOUND TIME LIGHT YOU */}
      {loadingComplete && (
        <TypingText
          text="SPACE SOUND TIME LIGHT YOU"
          delay={2500}
          speed={60}
          style={{
            position: 'absolute',
            bottom: line1Offset,
            left: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}

      {/* Bas droite - SPACE UNDERCONSTRUCTION */}
      {loadingComplete && (
        <TypingText
          text="SPACE UNDERCONSTRUCTION / 2026  /"
          delay={4200}
          speed={50}
          style={{
            position: 'absolute',
            bottom: offset,
            right: offset,
            fontSize,
            fontFamily: "'Codystar', cursive"
          }}
        />
      )}
    </div>
  )
}

function Root() {
  const [loadingComplete, setLoadingComplete] = useState(false);

  // Choisir le bon composant selon l'appareil
  const AppComponent = isLowEndAndroid ? AppAndroid : App;

  return (
    <>
      {/* <Stats /> */}
      <AppComponent onLoadingComplete={() => setLoadingComplete(true)} />
      <Overlay loadingComplete={loadingComplete} />
    </>
  );
}

createRoot(document.getElementById('root')).render(<Root />)
   